import { useEffect } from "react";

/**
 * Lightweight per-page SEO updater. Imperatively sets <title>, meta description,
 * canonical URL, and Open Graph / Twitter card meta tags.
 *
 * No external deps (avoids react-helmet-async). Works for client-side crawlers
 * (Google, Bingbot, Slack/WhatsApp/Twitter share-preview bots — all fetch the
 * rendered page and read these tags).
 *
 * On unmount it intentionally does NOT restore the previous values — the next
 * page's <PageSEO> call will overwrite. This is fine for SPA navigation.
 */
export default function PageSEO({
  title,
  description,
  image,
  url,
  type = "website",
  noIndex = false,
}) {
  useEffect(() => {
    if (title) document.title = title;
    document.documentElement.dataset.pageSeo = "1";
    setMeta({ name: "description" }, description);
    setMeta({ name: "robots" }, noIndex ? "noindex, nofollow" : "index, follow");

    setMeta({ property: "og:type" }, type);
    setMeta({ property: "og:title" }, title);
    setMeta({ property: "og:description" }, description);
    setMeta({ property: "og:image" }, image);
    setMeta({ property: "og:url" }, url);

    setMeta({ name: "twitter:card" }, image ? "summary_large_image" : "summary");
    setMeta({ name: "twitter:title" }, title);
    setMeta({ name: "twitter:description" }, description);
    setMeta({ name: "twitter:image" }, image);

    // Canonical
    if (url) {
      let link = document.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        document.head.appendChild(link);
      }
      link.setAttribute("href", url);
    }
  }, [title, description, image, url, type, noIndex]);

  return null;
}

function setMeta(selectorAttrs, content) {
  if (content === undefined || content === null || content === "") return;
  const [attrKey, attrVal] = Object.entries(selectorAttrs)[0];
  let el = document.head.querySelector(`meta[${attrKey}="${attrVal}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attrKey, attrVal);
    document.head.appendChild(el);
  }
  el.setAttribute("content", String(content));
}
